import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, School } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import AcademicHierarchyHelper from "../components/AcademicHierarchyHelper";
import { useAuth } from "../contexts/AuthContext";
import { localName } from "../lib/localName";

const EMPTY = { grade_id: "", name: "", capacity: 30 };

export default function ClassesPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [schools, setSchools] = useState([]);
  const [grades, setGrades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [school, setSchool] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => {
        if (prev) return prev;
        if (!isSuper && user?.school_id && list.some(s => s.id === user.school_id)) return user.school_id;
        return list[0]?.id || "";
      });
    });
  }, [user?.school_id, isSuper]);
  const load = () => {
    if (school) {
      api.get(`/grades?school_id=${school}`).then(r => setGrades(r.data || []));
      api.get(`/classes?school_id=${school}`).then(r => setClasses(r.data || []));
    }
  };
  useEffect(() => { load(); }, [school]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };
  const submit = async () => {
    const payload = { ...form, school_id: school, capacity: parseInt(form.capacity) || 30 };
    if (editing) await api.put(`/classes/${editing.id}`, payload);
    else await api.post("/classes", payload);
    toast.success(t("success")); setOpen(false); load();
  };
  const remove = async (it) => { await api.delete(`/classes/${it.id}`); toast.success(t("success")); load(); };

  return (
    <div className="space-y-6">
      <AcademicHierarchyHelper highlight="class" />
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("classes")}</h1>
        <div className="flex gap-3 flex-wrap">
          <Select value={school} onValueChange={setSchool} disabled={!isSuper}>
            <SelectTrigger className="w-56" data-testid="classes-school-select"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
            <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} disabled={!school || grades.length === 0} data-testid="new-class-btn"><Plus className="w-4 h-4 me-2" />{t("new_class")}</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{editing ? t("edit") : t("new_class")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("select_grade")}</Label>
                  <Select value={form.grade_id} onValueChange={v => setForm({ ...form, grade_id: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{grades.map(g => <SelectItem key={g.id} value={g.id}>{localName(g, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("name")}</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="A / 1" /></div>
                <div><Label>{t("capacity")}</Label><Input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
                <Button onClick={submit} className="w-full" data-testid="class-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {classes.map(c => (
          <Card key={c.id} className="p-5 border shadow-none card-hover smooth" data-testid={`class-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/10 text-secondary grid place-items-center"><School className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{localName(c, lang)}</div>
                <div className="text-xs text-muted-foreground truncate">{localName(grades.find(g => g.id === c.grade_id), lang)} • {c.capacity}</div>
              </div>
              <RowActions item={c} onEdit={openEdit} onDelete={remove} testIdPrefix="class" />
            </div>
          </Card>
        ))}
        {classes.length === 0 && <Card className="p-8 col-span-full text-center text-sm text-muted-foreground border-dashed">{t("no_data")}</Card>}
      </div>
    </div>
  );
}
